export default {
  howItWorksPage: {
    seoTitle: 'How Geofleet Works — From Order to Proof of Delivery',
    seoDescription:
      'See how Geofleet works in six steps: import orders, plan optimized routes, dispatch to the driver app, track live, send customers a tracking link, and capture proof of delivery — all from one command centre with an AI Copilot.',
    heroBadge: 'How it works',
    heroTitle: 'How Geofleet works:',
    heroTitleHighlight: 'order in, proof of delivery out',
    /** Direct answer paragraph — written to be quotable by search and AI answer engines. */
    heroLead:
      'Geofleet is delivery management software. You bring orders in from a CSV, your store, or an API. Geofleet plans optimized routes and sends them to drivers’ phones. From there you track every stop live, text customers a tracking link, and collect photo, signature, or OTP proof of delivery. It all runs from one command centre, and an AI Copilot answers questions about the same live data.',
    heroFacts: [
      { icon: 'ri-price-tag-3-line', label: 'From $49/month for 3 drivers' },
      { icon: 'ri-smartphone-line', label: 'Driver app works offline' },
      { icon: 'ri-link', label: 'No app install for customers' },
    ],
    stepsKicker: 'The delivery lifecycle',
    stepsTitle: 'Six steps from order to proof of delivery',
    stepsIntro: 'Every order goes through the same six steps in Geofleet, whether you run 5 drivers or 500.',
    stepLabel: 'Step {{n}}',
    steps: [
      {
        title: 'Bring your orders in',
        body: 'Upload a CSV or spreadsheet, sync orders automatically from Shopify and other OMS platforms, push them over the API or MCP, or add them by hand. Addresses are geocoded on import so they are ready to route.',
        points: ['CSV and spreadsheet templates', 'Shopify, Unicommerce and 10+ OMS integrations', 'REST API and MCP for your own systems'],
        linkLabel: 'See integrations',
        linkHref: '/integrations',
        imageAlt: 'Uploading a CSV of delivery orders into Geofleet',
      },
      {
        title: 'Plan optimized routes',
        body: 'Pick a planning profile, such as “Tomorrow Deliveries”, and generate a smart plan. The route engine accounts for time windows, vehicle capacity, traffic and driver workload, then groups stops into trips.',
        points: ['Time windows and shift limits', 'Weight, volume and item capacity', 'Balanced workloads across drivers'],
        linkLabel: 'Route planner',
        linkHref: '/features/route-planner',
        imageAlt: 'Geofleet Smart Planning generating optimized routes from a planning profile',
      },
      {
        title: 'Dispatch to drivers',
        body: 'Review each trip’s distance, duration and order count, then publish it. The route arrives in the driver app with turn-by-turn navigation. On Premium, AI auto-dispatch and auto-allocation assign trips for you.',
        points: ['One-click publish to the driver app', 'Works offline and syncs on reconnect', 'AI auto-dispatch on Premium'],
        linkLabel: 'Driver app',
        linkHref: '/features/driver-app',
        imageAlt: 'A published trip being dispatched to a driver in Geofleet',
      },
      {
        title: 'Track live and adjust',
        body: 'Every vehicle and stop is on one live map. Late or at-risk stops move to the top of the queue. You can resequence stops, reassign orders or add a stop mid-route, and the driver’s app updates right away.',
        points: ['Live map with planned vs actual route', 'Exceptions and SLA risk surfaced first', 'Drag-and-drop resequencing'],
        linkLabel: 'Reports & analytics',
        linkHref: '/features/reports-analytics',
        imageAlt: 'Resequencing delivery orders on a live route map in Geofleet',
      },
      {
        title: 'Keep customers informed',
        body: 'Customers get a tracking link by SMS, email or WhatsApp. It opens in any browser and shows live status, ETA and the driver on a map, so they don’t need to install anything. On Premium, the page carries your own branding.',
        points: ['Automatic updates at key milestones', 'Live ETA and driver contact', 'Branded tracking page on Premium'],
        linkLabel: 'Branded tracking',
        linkHref: '/features/branded-tracking',
        imageAlt: 'A customer following a live Geofleet delivery tracking link on their phone',
      },
      {
        title: 'Prove delivery and measure',
        body: 'Drivers capture a photo, e-signature, OTP or barcode scan at the door. Each proof is timestamped and geotagged. Results feed on-time rate, cost per drop and driver scorecards, so you can see what to fix tomorrow.',
        points: ['Photo, signature, OTP and barcode', 'Searchable, exportable proof records', 'On-time, cost-per-drop and driver KPIs'],
        linkLabel: 'Proof of delivery',
        linkHref: '/features/proof-of-delivery',
        imageAlt: 'Driver capturing photo proof of delivery in the Geofleet driver app',
      },
    ],
    surfacesKicker: 'Where you run it',
    surfacesTitle: 'One command centre, with Copilot built in',
    surfacesIntro:
      'All six steps happen in one place. The command centre is the live view of your operation. Copilot is an AI assistant that reads the same data and can act on it within your permissions.',
    towerSectionTitle: 'Command centre',
    towerLead: 'Orders, drivers, the live map, ETAs and exceptions on one screen, so the next action is obvious.',
    towerFeatureCards: [
      {
        title: 'Bring data in your way',
        description: 'Integrations, MCP, CSV templates or manual entry, all landing in the same order list.',
      },
      {
        title: 'Plan, assign and adapt',
        description: 'Reassign, resequence, inject stops and run bulk updates within role and policy guardrails.',
      },
      {
        title: 'One map, prioritized queues',
        description: 'Exceptions rise to the top, and each role sees its own slice: dispatch, hub leads or partners.',
      },
      {
        title: 'Measure and audit',
        description: 'On-time rate, cost per drop and scorecards, with a full record of who changed what and when.',
      },
    ],
    copilotSectionTitle: 'Copilot',
    copilotLead: 'Ask about your operation in plain language. Answers come from live data, not generic AI text.',
    copilotFeatureCards: [
      {
        title: 'Ask in plain language',
        description: '“Which stops in the north zone are running late?” or “What changed since 09:00?”',
      },
      {
        title: 'Operational answers',
        description: 'Filtered lists, comparisons and summaries for dispatch, routing and exceptions.',
      },
      {
        title: 'Suggested actions, with guardrails',
        description: 'Copilot proposes reroutes or bulk updates for you to confirm. It cannot do anything your role can’t.',
      },
      {
        title: 'Faster onboarding',
        description: 'Guided prompts to set up hubs, integrations and workflows without spreadsheet choreography.',
      },
    ],
    faqKicker: 'FAQ',
    faqTitle: 'Questions about how Geofleet works',
    faqs: [
      {
        question: 'How does Geofleet work?',
        answer:
          'Geofleet takes delivery orders from a CSV, your store or an API and plans optimized routes. It dispatches those routes to a driver app, tracks each stop live and sends customers a tracking link. At the door, drivers capture photo, signature or OTP proof of delivery. Dispatchers run everything from one command centre, with an AI Copilot for questions and bulk actions.',
      },
      {
        question: 'How do I get my orders into Geofleet?',
        answer:
          'There are four ways. You can upload a CSV or spreadsheet, connect an OMS or store such as Shopify or Unicommerce so orders sync automatically, send orders through the REST API or MCP, or add them manually. You can mix methods: many teams start with CSV and add an integration later.',
      },
      {
        question: 'Do drivers need to install an app?',
        answer:
          'Yes. Drivers use the Geofleet driver app to receive routes, navigate, capture proof of delivery and record attendance. The app works offline and syncs when the phone reconnects.',
      },
      {
        question: 'Do customers need to install anything to track a delivery?',
        answer:
          'No. Customers get a tracking link by SMS, email or WhatsApp that opens in any browser. It shows live status, ETA and the driver’s location.',
      },
      {
        question: 'Can Copilot change orders or routes on its own?',
        answer:
          'Only within the permissions you set. Copilot follows role-based access, so it can only do what the signed-in user is allowed to do. It proposes actions such as reroutes or bulk updates for you to confirm.',
      },
      {
        question: 'How much does Geofleet cost to try?',
        answer:
          'Plans start with Geofleet Lite at $49 a month for 3 drivers and 500 orders, then contact us for additional capacity. Professional is $19 and Premium is $29 per driver per month, billed quarterly. Professional has a 14-day free trial.',
      },
    ],
    visualHeroAlt: 'Geofleet command centre with live map, KPIs and order list',
    visualCommandAlt: 'Geofleet command centre — live map and operations view',
    visualCopilotAlt: 'Geofleet Copilot answering a dispatch question next to a live route',
    closingTitle: 'See your own orders move through it',
    closingSub: 'Start with a CSV of real orders, or book a demo and we’ll walk through your workflow.',
  },
};
